import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthLayout } from '@/components/AuthLayout';
import { Button, Checkbox, Field, Input } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError, tokenStorage } from '@/lib/api';
import { resolveLandingPathWithSet, useStoredLandingPath } from '@/lib/use-landing-page';
import { useAuth } from '@/lib/use-auth';

// No icon library for three glyphs; paths are Lucide's, on a 24x24 stroked viewBox.
const STROKE = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function EyeIcon() {
  return (
    <svg {...STROKE} className="h-4 w-4" aria-hidden="true">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg {...STROKE} className="h-4 w-4" aria-hidden="true">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

export function LoginPage() {
  const { user, ability, login } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [remember, setRemember] = useState(tokenStorage.getRemembered);

  /*
   * The deep link `RequireAuth` captured on the way past, if there was one. It
   * beats every preference: the person asked for a specific page and was sent
   * here only because they were not signed in yet.
   */
  const from = (location.state as { from?: string } | null)?.from;

  /*
   * ⚠️ Fetched only when it will actually be read — signed in, and with no deep
   * link to honour. See `useStoredLandingPath`.
   */
  const landing = useStoredLandingPath(user !== null && from === undefined);

  /*
   * ── THE ONE REDIRECT ────────────────────────────────────────────────────
   *
   * ⚠️ **There used to be TWO, and they were a matched pair nothing checked.**
   * One here for somebody already signed in who opens `/login`, and one inside
   * `handleSubmit` firing `navigate()` on a successful sign-in. Both named
   * `/dashboard` as a literal, and when that page was deleted **both
   * typechecked perfectly and landed on the 404** — there is no type, no test
   * and no lint rule in this repo that can see a route string. Merging them
   * means the destination is decided in one expression, so the next move can
   * only be wrong once.
   *
   * ⚠️ **This is also why `handleSubmit` no longer navigates.** Resolving the
   * landing page needs the FRESH `ability`, and `handleSubmit` is a closure
   * over the render that created it: after `await login(...)` the `ability` in
   * its scope is still the signed-out one, so a redirect decided there would
   * gate against no permissions at all and could send an administrator to the
   * wrong page. Deciding at RENDER, after `user` lands, is what makes the
   * permission test meaningful rather than merely present.
   *
   * While the preference is in flight this holds the sign-in screen rather than
   * flashing a page and replacing it. `useStoredLandingPath` caps that wait and
   * settles to the default, so it is bounded even if `/preferences` never
   * answers.
   */
  if (user) {
    if (from !== undefined) return <Navigate to={from} replace />;
    if (landing.status === 'settled') {
      /*
       * ⚠️ **`resolveLandingPathWithSet`, NOT `resolveLandingPath` — and this
       * screen is the SECOND of two callers that must agree.** `App.tsx`'s
       * `LandingRedirect` is the other; if only one of them is widened, a person
       * signing in and a person hitting `/` land on DIFFERENT pages, with
       * nothing on either screen to say why and nothing that fails.
       *
       * The third argument is the landing page this person's permission SETS
       * name, resolved server-side. It is a DEFAULT and loses to `landing.stored`
       * — the reader's own choice — which is the ladder that function owns.
       */
      return (
        <Navigate
          to={resolveLandingPathWithSet(landing.stored, ability.landingPath, ability)}
          replace
        />
      );
    }
  }

  const fieldErrors = error instanceof ApiError ? error.fieldErrors : {};
  // A validation message with an EMPTY path (a `.strict()` schema's unrecognised-key
  // rejection) binds no field, so it must render as the banner, not via fieldErrors.
  const formMessage = error instanceof ApiError ? error.formMessage : null;
  const bannerMessage =
    formMessage ?? (Object.keys(fieldErrors).length > 0 ? null : (error?.message ?? null));

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      /*
       * No `navigate()` here on purpose — the redirect above owns the
       * destination, and it runs on the next render with the real `ability` in
       * hand. Sign-in succeeding is what sets `user`, which is what triggers it.
       */
      await login(email, password, remember);
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error('Something went wrong'));
    } finally {
      setSubmitting(false);
    }
  }

  /*
   * The lockup above the form already says SEED EMERGE, and the fields say the
   * rest — so the heading is kept for screen readers and taken off the screen,
   * and the "Use your SEED work email." line is gone outright. Owner's call.
   */
  return (
    <AuthLayout title="Sign in to SEED EMERGE" hideHeading>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Field label="Work email" required error={fieldErrors.email}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              invalid={Boolean(fieldErrors.email)}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <Field label="Password" required error={fieldErrors.password}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="••••••••"
              required
              invalid={Boolean(fieldErrors.password)}
              aria-describedby={describedBy}
              trailingSlot={
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  leadingIcon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
                />
              }
            />
          )}
        </Field>

        <p className="m-0 -mt-1 text-right text-xs">
          <TextLink to="/forgot-password" className="font-medium">
            Forgot password?
          </TextLink>
        </p>

        <Checkbox
          checked={remember}
          onChange={(event) => setRemember(event.target.checked)}
          label="Keep me signed in"
        />

        {bannerMessage && (
          <Notice tone="danger" role="alert" className="break-words">
            {bannerMessage}
          </Notice>
        )}

        {/*
          ⚠️ Still loading once `user` is set. Sign-in has succeeded but the
          landing page has not settled yet, so this render is the gap between
          the two — and a button that snaps back to "Sign in" in that gap reads
          as a sign-in that silently failed.
        */}
        <Button
          type="submit"
          variant="primary"
          loading={submitting || user !== null}
          className="w-full"
        >
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
